import type { NavItem, NavDrillGroup } from '../NavBar/nav-types';
import {
  CONFIGURE_GROUP_ORDER,
  CONFIGURE_HUB_ROOTS,
  type ConfigureGroupLabel,
  HIDDEN_CONFIGURE_PATHS,
  orgRelativePath,
  resolveConfigureSection,
} from './configure-sections';

export interface ConfigureNavGroup {
  /** Group heading; an empty label renders the items with no heading. */
  label: string;
  items: NavItem[];
}

/**
 * Build the flat Configure nav from the configure-surface drill groups
 * (already permission-filtered and slug-scoped by `useNavSources`).
 *
 * Items are bucketed through {@link resolveConfigureSection}: the mapping
 * table decides group and label, unmapped items fall back by drill-group
 * label, and hidden pages (extra Credentials pages, Agent Types, per-provider
 * Integrations) stay out of the nav while remaining routable.
 *
 * The Integrations overview page IS a nav row (unlike the old settings nav,
 * which hid it in favour of the provider list). Provider sub-drill items and
 * plugin detail pages stay out of the nav while remaining routable.
 */
export function buildConfigureNav(
  slug: string,
  configureDrillGroups: readonly NavDrillGroup[],
): ConfigureNavGroup[] {
  const buckets = new Map<ConfigureGroupLabel, NavItem[]>(
    CONFIGURE_GROUP_ORDER.map((g) => [g, []]),
  );

  for (const group of configureDrillGroups) {
    // The Integrations sub-drill lists per-provider pages. Those stay
    // routable but out of the nav: the single Integrations overview row
    // (from the Organization drill) is the entry point.
    if (group.parentBasePath) continue;

    for (const item of group.items) {
      const relative = orgRelativePath(item.to, slug);
      if (HIDDEN_CONFIGURE_PATHS.has(relative)) continue;
      // Per-provider integrations and plugin detail: skip; Integrations covers Connect.
      if (
        relative.startsWith('/org/integrations/') ||
        relative.startsWith('/configure/connect/integrations/') ||
        relative.startsWith('/configure/connect/plugins/')
      )
        continue;

      const mapping = resolveConfigureSection(relative, group.label);
      if (!mapping) continue;

      const hubRoot = CONFIGURE_HUB_ROOTS[relative];
      const entry: NavItem = {
        ...item,
        ...(hubRoot ? { to: `/orgs/${slug}${hubRoot}` } : {}),
        label: mapping.label ?? item.label,
      };
      const bucket = buckets.get(mapping.group);
      // Organization hub is pinned first in its group.
      if (relative === '/configure/organization') bucket?.unshift(entry);
      else bucket?.push(entry);
    }
  }

  return CONFIGURE_GROUP_ORDER.filter((label) => (buckets.get(label) ?? []).length > 0).map(
    (label) => ({ label, items: buckets.get(label) ?? [] }),
  );
}
